import type { PortalConfig } from './types';

type Translator = (key: string, params?: Record<string, string | number>) => string;

const LANGUAGE_NAMES: Record<string, string> = {
  hi: 'हिंदी',
  en: 'English',
  sa: 'संस्कृत',
  as: 'অসমীয়া',
  bn: 'বাংলা',
  brx: 'बड़ो',
  doi: 'डोगरी',
  gu: 'ગુજરાતી',
  kn: 'ಕನ್ನಡ',
  ks: 'کٲشُر',
  kok: 'कोंकणी',
  mai: 'मैथिली',
  ml: 'മലയാളം',
  mni: 'মৈতৈলোন্',
  mr: 'मराठी',
  ne: 'नेपाली',
  or: 'ଓଡ଼ିଆ',
  pa: 'ਪੰਜਾਬੀ',
  sat: 'ᱥᱟᱱᱛᱟᱲᱤ',
  sd: 'سنڌي',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  ur: 'اردو',
};

const NAV_KEYS: Record<string, string> = {
  '/': 'nav.home',
  '/curriculum': 'nav.curriculum',
  '/curriculum/spokensanskritgame': 'nav.game',
  '/curriculum/navagrahchakra': 'nav.chakra',
  '/products/tools/game': 'nav.products',
  '/sangati': 'nav.sangati',
  '/journey': 'nav.journey',
  '/careers': 'nav.careers',
  '/products': 'nav.products',
  '/about': 'nav.about',
  '/contact': 'nav.contact',
  '/privacy': 'nav.privacy',
};

const SERVICE_KEYS: Record<string, { name: string; description: string }> = {
  'राजृषि शिक्षा अभ्यासक्रम': {
    name: 'service.curriculum.name',
    description: 'service.curriculum.description',
  },
  'षड्भिर्मासैः फलं संवत्सरेण सिद्धिं च': {
    name: 'service.tutor.name',
    description: 'service.tutor.description',
  },
  'वाद': {
    name: 'service.journey.name',
    description: 'service.journey.description',
  },
  'अर्थसाधक कर्म': {
    name: 'service.rubric.name',
    description: 'service.rubric.description',
  },
};

function translate(t: Translator, key: string, fallback: string): string {
  const value = t(key);
  return value === key ? fallback : value;
}

export function localizePortalConfig(config: PortalConfig, t: Translator, locales: string[]): PortalConfig {
  return {
    ...config,
    site_metadata: {
      ...config.site_metadata,
      title: translate(t, 'portal.title', config.site_metadata.title),
      tagline: translate(t, 'portal.tagline', config.site_metadata.tagline),
      description: translate(t, 'portal.description', config.site_metadata.description),
    },
    navigation: config.navigation.map((item) => ({
      ...item,
      label: NAV_KEYS[item.path] ? t(NAV_KEYS[item.path]) : item.label,
    })),
    service_offerings: config.service_offerings.map((offering) => {
      const key = SERVICE_KEYS[offering.id];
      return {
        ...offering,
        name: key ? t(key.name) : offering.name,
        description: key ? t(key.description) : offering.description,
      };
    }),
    languages: locales.map((code) => ({
      code,
      name: translate(t, `locale.${code}`, LANGUAGE_NAMES[code] || code),
      flag: config.languages.find((lang) => lang.code === code)?.flag ?? '🌐',
    })),
    footer: {
      ...config.footer,
      links: config.footer.links.map((link) => ({
        ...link,
        label: NAV_KEYS[link.url] ? t(NAV_KEYS[link.url]) : link.label,
      })),
      copyright: translate(t, 'footer.copyright', config.footer.copyright),
    },
  };
}
